import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components/macro';
import { darkTheme, lightTheme } from 'styles-global';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { muiDarkTheme, muiLightTheme } from 'utils/styleConstants';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  theme: typeof lightTheme;
}

const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  toggleTheme: () => void 0,
  theme: lightTheme,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // First check localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme !== null) {
      return savedTheme === 'dark';
    }
    // Then check browser preference
    return window?.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
  });

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    const mediaQuery = window?.matchMedia?.('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('theme') === null) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery?.addEventListener('change', handleChange);
    return () => mediaQuery?.removeEventListener('change', handleChange);
  }, []);

  return (
    <MuiThemeProvider theme={isDarkMode ? muiDarkTheme : muiLightTheme}>
      <ThemeContext.Provider value={{ isDarkMode, toggleTheme, theme: isDarkMode ? darkTheme : lightTheme }}>
        <StyledThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>{children}</StyledThemeProvider>
      </ThemeContext.Provider>
    </MuiThemeProvider>
  );
};
